import { MRT_Column, MRT_ColumnDef, MRT_TableInstance } from "material-react-table";
import { NonZeroBalanceLedger } from "./types";
import { Chip, IconButton, Link, Stack, Box, Typography, Tooltip } from "@mui/material";
import { fMonthDayYearDotNotation } from "../../utils/formatTime";
import { OrderNumberCell } from "../OrderNumberCell";
import { getOrderNumberDisplay, orderNumberFilterFn } from "../ReportsUtils";
import { fCurrency } from "../../utils/formatNumber";
import { useState } from "react";
import axios from "axios";
import dayjs from "dayjs";
import Iconify from "../../../minimals/components/iconify";
import { DatePicker } from "@mui/x-date-pickers";
import SingleDateRangeFilter from "./SingleDateRangeFilter";
import { createAutocompleteColumn } from "../generateAutocompleteColumnAndFilter";
import { formatUTCDateWithLocalTime, parseMs } from "../../../utils";

type LedgerNote = NonZeroBalanceLedger["notes"][number];

const getNoteText = (note?: LedgerNote | null) => note?.text ?? "";

const getAssignedToEmail = (row: NonZeroBalanceLedger) => {
  const assignedToEmail = row.assignedToEmail?.trim().toLowerCase() === "null" ? "" : row.assignedToEmail?.trim();
  return assignedToEmail || row.assignedToUser?.email || "";
};

const getFileNumber = (row: NonZeroBalanceLedger) => row.orderInfo?.orderNumber || row.name;

const getMailto = (email: string | null | undefined, row: NonZeroBalanceLedger) => {
  if (!email) return "";

  const fileNumber = getFileNumber(row);
  const subject = fileNumber ? `File ${fileNumber}` : "Deposit dashboard";

  return `mailto:${email}?subject=${encodeURIComponent(subject)}`;
};

const getAssignedToMailto = (row: NonZeroBalanceLedger) => getMailto(getAssignedToEmail(row), row);

const isAutomatedNote = (note: LedgerNote) => getNoteText(note).trimStart().startsWith("Assigned to");

const getNoteTime = (note: LedgerNote) => parseMs(note.dateCreated);

const getNoteDate = (note?: LedgerNote | null) => {
  if (!note) return null;
  const time = getNoteTime(note);
  return time === -Infinity ? null : new Date(time);
};

const getNewestNonAutomatedNote = (notes: LedgerNote[] = []) =>
  [...notes]
    .filter((note) => getNoteText(note).trim() && !isAutomatedNote(note))
    .sort((a, b) => getNoteTime(b) - getNoteTime(a))[0] ?? null;

const isCanceledOrder = (order: NonZeroBalanceLedger) => order.orderStatus?.toLowerCase().includes("cancel") ?? false;

const DueDateColumn = ({ order }: { order: NonZeroBalanceLedger }) => {
  const [isEditMode, setIsEditMode] = useState(false);
  const [editDueDate, setEditDueDate] = useState(dayjs(order.dueDate).format("YYYY-MM-DD"));
  const [isUpdating, setIsUpdating] = useState(false);

  const updateDueDate = async () => {
    var newDueDate = dayjs(editDueDate).toDate();
    setIsUpdating(true);
    await axios.post("/proxy/api/ledgers/editLedgerDueDate", {
      orderNumber: order.name,
      dueDate: newDueDate
    });
    order.dueDate = newDueDate;
    setIsUpdating(false);
    setIsEditMode(false);
  };

  const getUI = () => {
    if (!isEditMode) {
      return (
          <>
            {order.dueDate && <Chip label={fMonthDayYearDotNotation(order.dueDate)} />}
            <IconButton sx={{ marginLeft: 1 }} onClick={() => setIsEditMode(true)}>
              <Iconify icon="eva:edit-outline" />
            </IconButton>
          </>
      );
    }
    if (isUpdating) {
      return <h4>Updating....</h4>;
    }
    return (
        <Stack direction="row" alignItems="center">
          <DatePicker
              label="Due Date"
              onChange={(newValue) => setEditDueDate(dayjs(newValue!).format("YYYY-MM-DD"))}
              value={dayjs(editDueDate).toDate()}
              slotProps={{ textField: { sx: { width: 160, marginRight: 2 }, size: "small" } }}
          />
          <Box>
            <IconButton onClick={updateDueDate} color="success">
              <Iconify icon="eva:checkmark-fill" />
            </IconButton>
          </Box>
          <Box>
            <IconButton onClick={() => setIsEditMode(false)} color="error">
              <Iconify icon="eva:close-fill" />
            </IconButton>
          </Box>
        </Stack>
    );
  };

  return getUI();
};

export const columns: MRT_ColumnDef<NonZeroBalanceLedger>[] = [
  {
    id: "notes",
    header: "",
    enableColumnFilter: false,
    enableColumnActions: false,
    enableColumnDragging: false,
    enableColumnFilterModes: false,
    size: 50,
    enableHiding: false
  },
  {
    id: "newestNote",
    header: "Newest Note",
    accessorFn: (row) => getNoteDate(getNewestNonAutomatedNote(row.notes ?? [])),
    sortingFn: "datetime",
    enableColumnFilter: false,
    Cell: ({ row }) => {
      const displayNote = getNewestNonAutomatedNote(row.original.notes ?? []);
      const noteText = getNoteText(displayNote).trim();

      if (!displayNote) return "";

      return (
        <Box>
          <Typography fontWeight={600}>{formatUTCDateWithLocalTime(displayNote.dateCreated)}</Typography>
          <Tooltip title={noteText} placement="top">
            <Typography
              sx={{
                maxWidth: "250px",
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
                cursor: "pointer",
                mt: 0.5
              }}
            >
              {noteText}
            </Typography>
          </Tooltip>
        </Box>
      );
    },
    size: 250
  },
  {
    header: "Order Number",
    id: "orderNumber",
    accessorFn: getOrderNumberDisplay,
    filterFn: orderNumberFilterFn,
    Cell: OrderNumberCell,
    muiFilterTextFieldProps: {
      variant: "outlined",
      size: "small"
    },
    size: 50
  },
  createAutocompleteColumn<NonZeroBalanceLedger>("Department", "orderInfo.department", {
    placeholder: "Department",
    cellPaddingRight: 3,
    size: 150
  }),
  {
    id: "dueDate",
    header: "Due Date",
    accessorFn: (row) => (row.dueDate ? new Date(row.dueDate) : null),
    filterVariant: "date",
    sortingFn: "datetime",
    Cell: ({ row }) => <DueDateColumn order={row.original} />,
    filterFn: (row, id, filterValue) => {
      if (filterValue[0] || filterValue[1]) {
        const dueDate = new Date(row.original.dueDate).getTime();
        const startDate = filterValue[0] ? new Date(filterValue[0]).getTime() : null;
        const endDate = filterValue[1] ? new Date(filterValue[1]).getTime() : null;
        return (startDate ? dueDate >= startDate : true) && (endDate ? dueDate <= endDate : true);
      } else {
        return true;
      }
    },
    Filter: SingleDateRangeFilter,
    size: 230
  },
  {
    header: "Ledger",
    accessorKey: "friendlyName",
    muiFilterTextFieldProps: {
      variant: "outlined",
      size: "small"
    },
    Cell: ({ row, renderedCellValue }) => (
        <Link href={`/ledger/${row.original.name}`} target="_blank">
          {renderedCellValue}
        </Link>
    ),
    size: 100
  },
  {
    id: "for",
    header: "For",
    accessorKey: "ledgerComment",
    muiFilterTextFieldProps: {
      variant: "outlined",
      size: "small"
    },
    Cell: ({ row }) => (
        <Typography variant="body2" paddingRight={3}>
          {row.original.ledgerComment}
        </Typography>
    )
  },
  {
    header: "Amount",
    accessorKey: "amount",
    Cell: ({ row }) =>
        row.original.amount ? (
            <Link
                component="span"
                role="button"
                tabIndex={0}
                underline="always"
                onClick={() => (window as any).openIncomingWires?.(row.original.name)}
                onKeyDown={(event) => {
                  if (event.key === "Enter" || event.key === " ") {
                    event.preventDefault();
                    (window as any).openIncomingWires?.(row.original.name);
                  }
                }}
                sx={{ cursor: "pointer", userSelect: "text" }}
            >
              {fCurrency(row.original.amount)}
            </Link>
        ) : (
            <></>
        ),
    muiFilterTextFieldProps: {
      variant: "outlined",
      size: "small"
    },
    size: 100
  },
  {
    id: "settlementDate",
    header: "Settlement Date",
    accessorFn: (row) => (!isCanceledOrder(row) && row.settlementDate ? new Date(row.settlementDate) : null),
    filterVariant: "date",
    sortingFn: "datetime",
    filterFn: (row, id, filterValue) => {
      if (!filterValue[0] && !filterValue[1]) {
        return true;
      }

      if (isCanceledOrder(row.original) || !row.original.settlementDate) {
        return false;
      }

      const settlementDate = new Date(row.original.settlementDate).getTime();
      const startDate = filterValue[0] ? new Date(filterValue[0]).getTime() : null;
      const endDate = filterValue[1] ? new Date(filterValue[1]).getTime() : null;

      return (startDate ? settlementDate >= startDate : true) && (endDate ? settlementDate <= endDate : true);
    },
    Filter: SingleDateRangeFilter,
    Cell: ({ row }) => {
      if (isCanceledOrder(row.original)) {
        return <Typography variant="body2">Canceled</Typography>;
      }

      return row.original.settlementDate ? (
          <Typography variant="body2">{fMonthDayYearDotNotation(row.original.settlementDate)}</Typography>
      ) : null;
    },
    size: 150
  },
  {
    header: "Closer",
    accessorKey: "orderInfo.closer",
    Cell: ({ renderedCellValue = "", row }) => (
        <Link href={getMailto(row.original.orderInfo?.closerEmail, row.original)}>{renderedCellValue}</Link>
    ),
    muiFilterTextFieldProps: {
      variant: "outlined",
      size: "small"
    }
  },
  {
    header: "Assigned To",
    accessorKey: "assignedTo",
    Cell: ({ renderedCellValue = "", row }) => {
      const assignedToEmail = getAssignedToEmail(row.original);
      return assignedToEmail ? (
        <Link href={getAssignedToMailto(row.original)}>{renderedCellValue}</Link>
      ) : (
        <Typography variant="body2">{renderedCellValue}</Typography>
      );
    },
    muiFilterTextFieldProps: {
      variant: "outlined",
      size: "small"
    }
  },
  {
    header: "Funder",
    accessorKey: "orderInfo.funder",
    Cell: ({ renderedCellValue = "", row }) => (
        <Link href={getMailto(row.original.orderInfo?.funderEmail, row.original)}>{renderedCellValue}</Link>
    ),
    muiFilterTextFieldProps: {
      variant: "outlined",
      size: "small"
    }
  },
  {
    header: "Post Closer",
    accessorKey: "orderInfo.postCloserFullName",
    Cell: ({ renderedCellValue = "", row }) => (
        <Link href={getMailto(row.original.orderInfo?.postCloserEmail, row.original)}>{renderedCellValue}</Link>
    ),
    muiFilterTextFieldProps: {
      variant: "outlined",
      size: "small"
    }
  }
];
